// Carleton College: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Carleton's Common Data Set 2025-2026 (CDS-PDF-2025-2026_PDF_Carleton_
//    06242026.pdf), the file Institutional Research and Assessment links from
//    its Common Data Set page; the file itself sits on an Amazon S3 host, so
//    the SOURCES url is the carleton.edu page that links it. C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table; C8 test
//    policy for Fall 2027; C21/C22 early plans; G1/G5 2026-2027 costs (the
//    "not yet available" box is unchecked, so these are final).
//    Quirk: C21 prints "For the Fall 2024 entering class" above the early
//    decision counts (760 applied, 263 admitted). That line is the CDS form's
//    own label; the 2024-2025 edition carries the same label over different
//    counts (667 and 244), so the 2025-2026 counts are read as Fall 2025.
//  - Carleton Admissions, In Depth: Materials and Deadlines (Fall and Winter
//    Early Decision, Regular Decision, test policy, arts supplements). The
//    page carries no year; it is the page as posted for the application that
//    opened in August 2026 (it links the Class of 2030 profile).
//  - Carleton 2026-2027 Academic Catalog (majors; see the majors file).
//  - College Scorecard, school id 173258 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Carleton Common Data Set 2025-2026',
        url: 'https://www.carleton.edu/ira/institutional-data/institutional-data-cds/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Carleton Admissions, In Depth: Materials and Deadlines',
        url: 'https://www.carleton.edu/admissions/apply/steps/materials/',
        read: '2026-10-01',
    },
    catalog: {
        label: 'Carleton 2026-2027 Academic Catalog, Academic Requirements',
        url: 'https://www.carleton.edu/catalog/2026/requirements/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?173258-Carleton-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'carleton-college',
    kbId: 'carleton_college',
    name: 'Carleton College',
    shortName: 'Carleton',
    location: 'Northfield, MN',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Carleton college colors page); decoration only.
    branding: {
        primary: '#003069',
        secondary: '#FFD24F',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/carleton_college_logo.png',
    },

    faq: {
        answer: { text: 'Carleton College admitted 21.8% of first-year applicants for fall 2025, 1,480 of the 6,796 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the Carleton early decision acceptance rate?', a: 'For fall 2025, Carleton admitted 263 of 760 Early Decision applicants, 34.6%. The Common Data Set counts Fall and Winter Early Decision together.', source: 'cds', where: 'section C21' },
            { q: 'Does Carleton have early action?', a: 'No. Carleton\'s Common Data Set marks no early action plan. Its early rounds are Fall and Winter Early Decision.', source: 'cds', where: 'section C22' },
            { q: 'What are Carleton\'s application deadlines for fall 2027?', a: 'Fall Early Decision is November 15, with decisions December 15. Winter Early Decision and Regular Decision are both January 15, with decisions February 15 and April 1. If Early Decision admits you, you withdraw all other applications and enroll at Carleton.', source: 'deadlines' },
            { q: 'Is Carleton test-optional?', a: 'Yes. Carleton adopted a permanent test-optional policy in 2025: SAT or ACT scores are not required, but you may send them, and you may self-report them.', source: 'deadlines' },
            { q: 'Does Carleton have an application fee?', a: 'No. Carleton charges no application fee, and an interview is optional.', source: 'deadlines' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 6796,
        admitted: 1480,
        enrolled: 518,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 636, admitted: 221 },
            { group: 'Out-of-state', applied: 2870, admitted: 1127 },
            { group: 'International', applied: 3290, admitted: 132 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Carleton admits students to the college, not to a major, and its Common Data Set reports one first-year acceptance rate with no rate by major. Students choose a major in the third term of sophomore year.',
        source: 'catalog',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set gives one early decision count, Fall and Winter Early Decision together. Carleton has no early action plan.',
        rows: [
            { plan: 'Fall and Winter Early Decision', applied: 760, admitted: 263 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Carleton as a whole, with no separate school to choose. One optional piece:',
        rows: [
            { school: 'Arts supplements (optional)', requirement: 'You may send a portfolio in visual arts, music, theater, dance or film through your applicant status page after you apply; Carleton professors review it for the admissions committee. Creative writing portfolios are not accepted, and no portfolio is needed to major in the arts.' },
        ],
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Fall Early Decision', deadline: 'November 15', decision: 'December 15', binding: true },
            { plan: 'Winter Early Decision', deadline: 'January 15', decision: 'February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'April 1', binding: false },
        ],
        note: 'Financial aid materials are due on the same date as the application in each round. Admitted students confirm by January 15 (Fall Early Decision), March 1 (Winter Early Decision) or May 1 (Regular Decision). There is no application fee, and an interview is optional.',
        source: 'deadlines',
        binding: 'Early Decision is binding: if Carleton admits you, you withdraw all other applications and enroll at Carleton. You may file Regular Decision applications elsewhere while you wait. Deferred students are reconsidered in Regular Decision; denied students should not reapply in Regular Decision.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Carleton adopted a permanent test-optional policy in 2025: SAT or ACT scores are not required, but you may send them, and you may self-report them. The writing section is not required. If you send scores, they are due by November 20 for Fall Early Decision and January 20 for Winter Early Decision and Regular Decision.',
        source: 'deadlines',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 75186 },
            { item: 'Required fees', amount: 486 },
            { item: 'Housing (on campus)', amount: 10194 },
            { item: 'Food (on-campus meal plan)', amount: 9120 },
            { item: 'Books and supplies', amount: 1101 },
            { item: 'Transportation', amount: 850 },
            { item: 'Other expenses', amount: 939 },
        ],
        total: 97876,
        note: 'For a full-time student living on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 25295,
            byIncome: [
                { income: '$0 to $30,000', amount: 12275 },
                { income: '$30,001 to $48,000', amount: 11313 },
                { income: '$48,001 to $75,000', amount: 18924 },
                { income: '$75,001 to $110,000', amount: 18309 },
                { income: 'Over $110,000', amount: 37770 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Carleton\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
